'use client';

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
} from 'recharts';

interface Prediction {
  last_known_price: number;
  last_known_year: number;
  year_1: number;
  year_3: number;
  year_5: number;
  confidence_low_5yr: number;
  confidence_high_5yr: number;
  annual_growth_pct: number;
  r2: number;
}

interface Props {
  /** Last 5-8 years of actual median prices (for the transition) */
  recentYears: number[];
  recentPrices: number[];
  prediction: Prediction;
  district: string;
  propertyTypeLabel: string;
}

function formatGBP(v: number): string {
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}m`;
  if (v >= 1_000) return `£${Math.round(v / 1_000)}k`;
  return `£${v}`;
}

function r2Label(r2: number): { text: string; color: string; bg: string } {
  if (r2 >= 0.9) return { text: 'High', color: 'text-emerald-700', bg: 'bg-emerald-50' };
  if (r2 >= 0.7) return { text: 'Moderate', color: 'text-amber-700', bg: 'bg-amber-50' };
  return { text: 'Low', color: 'text-red-700', bg: 'bg-red-50' };
}

export default function PredictionCard({
  recentYears,
  recentPrices,
  prediction,
  district,
  propertyTypeLabel,
}: Props) {
  const { last_known_year: lastYr, annual_growth_pct: growth } = prediction;

  // Build combined dataset: actual years + projected years
  const data: Array<{
    year: number;
    actual?: number;
    projected?: number;
    low?: number;
    high?: number;
  }> = [];

  // Actual data points
  recentYears.forEach((yr, i) => {
    data.push({ year: yr, actual: recentPrices[i] });
  });

  // Projection points (1yr, 3yr, 5yr) with linear interpolation
  const projections = [
    { yr: lastYr + 1, price: prediction.year_1 },
    { yr: lastYr + 3, price: prediction.year_3 },
    { yr: lastYr + 5, price: prediction.year_5 },
  ];

  // Interpolate year 2 and year 4
  const yr2Price = Math.round((prediction.year_1 + prediction.year_3) / 2);
  const yr4Price = Math.round((prediction.year_3 + prediction.year_5) / 2);

  const allProj = [
    { yr: lastYr + 1, price: prediction.year_1 },
    { yr: lastYr + 2, price: yr2Price },
    { yr: lastYr + 3, price: prediction.year_3 },
    { yr: lastYr + 4, price: yr4Price },
    { yr: lastYr + 5, price: prediction.year_5 },
  ];

  // Connect last actual point to first projection
  if (data.length > 0) {
    const lastActual = data[data.length - 1];
    data[data.length - 1] = { ...lastActual, projected: lastActual.actual, low: lastActual.actual, high: lastActual.actual };
  }

  // Confidence band interpolation
  const lowStep =
    (prediction.confidence_low_5yr - prediction.last_known_price) / 5;
  const highStep =
    (prediction.confidence_high_5yr - prediction.last_known_price) / 5;

  allProj.forEach(({ yr, price }, i) => {
    const yearsOut = i + 1;
    data.push({
      year: yr,
      projected: price,
      low: Math.round(prediction.last_known_price + lowStep * yearsOut),
      high: Math.round(prediction.last_known_price + highStep * yearsOut),
    });
  });

  const { text: confText, color: confColor, bg: confBg } = r2Label(prediction.r2);
  const isPositive = growth >= 0;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-baseline justify-between">
        <h4 className="text-sm font-semibold text-[#3a3935]">
          5-Year Price Prediction
        </h4>
        <span
          className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${confBg} ${confColor}`}
        >
          Model confidence: {confText} (R²={prediction.r2.toFixed(2)})
        </span>
      </div>

      {/* Key numbers */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: '1 Year', value: prediction.year_1 },
          { label: '3 Years', value: prediction.year_3 },
          { label: '5 Years', value: prediction.year_5 },
        ].map(({ label, value }) => (
          <div
            key={label}
            className="rounded-lg border border-black/10 bg-[#faf8f4] p-2 text-center"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#7a7870]">
              {label}
            </p>
            <p className="text-sm font-bold text-[#0f0f0d]">{formatGBP(value)}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e4dc" />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: '#7a7870' }}
              tickLine={false}
              axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
            />
            <YAxis
              tickFormatter={formatGBP}
              tick={{ fontSize: 11, fill: '#7a7870' }}
              tickLine={false}
              axisLine={false}
              width={55}
            />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
              formatter={(value, name) => {
                const labels: Record<string, string> = {
                  actual: 'Actual',
                  projected: 'Projected',
                  low: 'Pessimistic',
                  high: 'Optimistic',
                };
                return [formatGBP(Number(value)), labels[String(name)] ?? String(name)];
              }}
            />

            {/* Confidence band */}
            <Area
              type="monotone"
              dataKey="high"
              stroke="none"
              fill="#d4ede5"
              fillOpacity={0.45}
            />
            <Area
              type="monotone"
              dataKey="low"
              stroke="none"
              fill="#ffffff"
              fillOpacity={1}
            />

            {/* Projected line */}
            <Area
              type="monotone"
              dataKey="projected"
              stroke="#2d7a5f"
              strokeWidth={2}
              strokeDasharray="6 3"
              fill="none"
            />

            {/* Actual line */}
            <Area
              type="monotone"
              dataKey="actual"
              stroke="#1a4a3a"
              strokeWidth={2.5}
              fill="#d4ede5"
              fillOpacity={0.35}
            />

            {/* Marker dots at 1yr, 3yr, 5yr */}
            {projections.map(({ yr, price }) => (
              <ReferenceDot
                key={yr}
                x={yr}
                y={price}
                r={4}
                fill="#2d7a5f"
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Growth summary */}
      <p className="text-xs text-[#7a7870]">
        Average annual growth:{' '}
        <span
          className={`font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}
        >
          {isPositive ? '+' : ''}
          {growth.toFixed(1)}%
        </span>{' '}
        | 5yr range: {formatGBP(prediction.confidence_low_5yr)} –{' '}
        {formatGBP(prediction.confidence_high_5yr)}
      </p>
    </div>
  );
}
